import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RtCtx = createContext({ subscribe: () => () => {}, connected: false });
const WS_URL = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`;

export function RealtimeProvider({ children }) {
  const { user, refreshUser, loadSettings } = useAuth();
  const listeners = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const uid = user ? user.id : null;

  useEffect(() => {
    if (!uid) return undefined;
    let ws;
    let stopped = false;
    let retry;
    let ping;
    let attempts = 0;
    const connect = () => {
      let opened = false;
      ws = new WebSocket(WS_URL);
      ws.onopen = () => {
        opened = true;
        attempts = 0;
        setConnected(true);
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
        listeners.current.forEach((fn) => fn({ type: "reconnect" }));
      };
      ws.onmessage = (m) => {
        let evt;
        try { evt = JSON.parse(m.data); } catch (e) { return; }
        if (evt.type === "wallet") refreshUser();
        if (evt.type === "settings") loadSettings().catch(() => {});
        listeners.current.forEach((fn) => fn(evt));
      };
      ws.onclose = async (e) => {
        setConnected(false);
        clearInterval(ping);
        if (stopped) return;
        if (e.code === 4401 || !opened) await api.post("/auth/refresh").catch(() => {});
        attempts += 1;
        retry = setTimeout(connect, Math.min(1000 * attempts, 8000));
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(ping);
      ws && ws.close();
    };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = (fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  };

  return <RtCtx.Provider value={{ subscribe, connected }}>{children}</RtCtx.Provider>;
}

export const useRealtime = () => useContext(RtCtx);

export function useLive(handler) {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe((e) => ref.current(e)), []); // eslint-disable-line react-hooks/exhaustive-deps
}
